import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { fetchTenants, fetchDeviceInventory, fetchBpcTenants } from '../api/client';
import Pagination from './Pagination';
import {
  formatDate,
  toDateStr,
  OSBadge,
  BoolCell,
  compareVals,
  applyColFilters,
  countActiveFilters,
  useResizableCols,
  TableHead,
} from './coverage/coverageShared';

// ── Column definitions ────────────────────────────────────────────────────────

const M365_COLS = [
  { key: 'deviceName',   label: 'Device Name',             w: 210, align: 'left',   filterType: 'text'    },
  { key: 'osType',       label: 'OS Type',                 w: 110, align: 'left',   filterType: 'multi-select' },
  { key: 'trustType',    label: 'Trust Type',              w: 110, align: 'left',   filterType: 'select'  },
  { key: 'isCompliant',  label: 'Intune Device Compliant', w: 155, align: 'center', filterType: 'bool'    },
  { key: 'isManaged',    label: 'Intune Managed',          w: 120, align: 'center', filterType: 'bool'    },
  { key: 'lastSeenUtc',  label: 'Last Seen',               w: 200, align: 'left',   filterType: 'date-op' },
  { key: 'lastUserUpn',  label: 'Last User',               w: 220, align: 'left',   filterType: 'text'    },
  { key: 'snapshotDate', label: 'Snapshot',                w: 130, align: 'left',   filterType: 'date'    },
];

const BPC_COLS = [
  { key: 'hostname',               label: 'Hostname',     w: 190, align: 'left',   filterType: 'text'    },
  { key: 'bpcTenant',              label: 'BPC Tenant',   w: 155, align: 'left',   filterType: 'select'  },
  { key: 'type',                   label: 'Type',         w:  90, align: 'left',   filterType: 'select'  },
  { key: 'osName',                 label: 'OS',           w:  90, align: 'left',   filterType: 'multi-select' },
  { key: 'osVersion',              label: 'OS Version',   w: 200, align: 'left',   filterType: 'text'    },
  { key: 'osIsEol',                label: 'EOL',          w:  65, align: 'center', filterType: 'bool'    },
  { key: 'firewallEnabled',        label: 'Firewall',     w:  80, align: 'center', filterType: 'bool'    },
  { key: 'windowsDefenderEnabled', label: 'Defender',     w:  85, align: 'center', filterType: 'bool'    },
  { key: 'encrypted',              label: 'Encrypted',    w:  90, align: 'center', filterType: 'bool'    },
  { key: 'agentVersion',           label: 'Agent Ver',    w: 100, align: 'left',   filterType: 'text'    },
  { key: 'agentLastSeenOn',        label: 'Last Seen',    w: 200, align: 'left',   filterType: 'date-op' },
  { key: 'collectionDate',         label: 'Snapshot',     w: 120, align: 'left',   filterType: 'date'    },
];

const DRMM_COLS = [
  { key: 'hostname',         label: 'Hostname',  w: 180, align: 'left',   filterType: 'text'    },
  { key: 'osCommonName',     label: 'OS',        w: 115, align: 'left',   filterType: 'multi-select' },
  { key: 'drmmInstance',     label: 'Instance',  w: 100, align: 'left',   filterType: 'select'  },
  { key: 'site',             label: 'Site',      w: 160, align: 'left',   filterType: 'text'    },
  { key: 'lastLoggedInUser', label: 'Last User', w: 210, align: 'left',   filterType: 'text'    },
  { key: 'online',           label: 'Online',    w:  80, align: 'center', filterType: 'bool'    },
  { key: 'lastSeen',         label: 'Last Seen', w: 200, align: 'left',   filterType: 'date-op' },
  { key: 'collectionDate',   label: 'Snapshot',  w: 130, align: 'left',   filterType: 'date'    },
];

// ── Filter state initializers ─────────────────────────────────────────────────

const initM365Filters = () => ({
  deviceName: '', osType: '', trustType: '', isCompliant: '', isManaged: '',
  lastSeenUtc: '', lastSeenUtc_op: '>=', lastUserUpn: '', snapshotDate: '',
});

const initDrmmFilters = () => ({
  hostname: '', osCommonName: '', drmmInstance: '', site: '', lastLoggedInUser: '',
  online: '', lastSeen: '', lastSeen_op: '>=', collectionDate: '',
});

const initBpcFilters = () => ({
  hostname: '', bpcTenant: '', type: '', osName: '', osVersion: '',
  osIsEol: '', firewallEnabled: '', windowsDefenderEnabled: '', encrypted: '',
  agentVersion: '', agentLastSeenOn: '', agentLastSeenOn_op: '>=', collectionDate: '',
});

// ── Main component ────────────────────────────────────────────────────────────

export default function CoverageAnalysisPage() {
  const [tenants, setTenants]               = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [workload, setWorkload]             = useState('m365');
  const [snapshotDate, setSnapshotDate]     = useState(() => new Date().toISOString().slice(0, 10));

  // Per-workload data state
  const [m365Devices, setM365Devices] = useState(null);
  const [drmmDevices, setDrmmDevices] = useState(null);
  const [bpcDevices,  setBpcDevices]  = useState(null);
  const [m365Loading, setM365Loading] = useState(false);
  const [drmmLoading, setDrmmLoading] = useState(false);
  const [bpcLoading,  setBpcLoading]  = useState(false);
  const [m365Error, setM365Error]     = useState(null);
  const [drmmError, setDrmmError]     = useState(null);
  const [bpcError,  setBpcError]      = useState(null);
  // Load time in seconds for display
  const [m365LoadTime, setM365LoadTime] = useState(null);
  const [drmmLoadTime, setDrmmLoadTime] = useState(null);
  const [bpcLoadTime,  setBpcLoadTime]  = useState(null);

  const [m365Filters, setM365Filters] = useState(initM365Filters());
  const [drmmFilters, setDrmmFilters] = useState(initDrmmFilters());
  const [bpcFilters,  setBpcFilters]  = useState(initBpcFilters());

  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir]     = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(10);

  // BPC tenant mappings (lazy — fetched on first Load click).
  const [bpcTenantsList, setBpcTenantsList] = useState(null);
  // Whether the currently-loaded BPC view is unmapped for the selected tenant.
  const [bpcUnmapped, setBpcUnmapped]       = useState(false);

  // Load tenants on mount
  useEffect(() => {
    fetchTenants()
      .then((data) => {
        const active = (Array.isArray(data) ? data : []).filter((t) => t.Status !== 'inactive');
        setTenants(active.sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')));
      })
      .catch(() => {})
      .finally(() => setTenantsLoading(false));
  }, []);

  // Reset sort when workload changes
  useEffect(() => {
    setSortField('');
    setSortDir('asc');
    setCurrentPage(1);
  }, [workload]);

  // Load all workloads in parallel
  const handleLoad = useCallback(async () => {
    setM365Devices(null);
    setDrmmDevices(null);
    setBpcDevices(null);
    setM365Loading(true);
    setDrmmLoading(true);
    setBpcLoading(true);
    setM365Error(null);
    setDrmmError(null);
    setBpcError(null);
    setM365LoadTime(null);
    setDrmmLoadTime(null);
    setBpcLoadTime(null);
    setBpcUnmapped(false);
    setM365Filters(initM365Filters());
    setDrmmFilters(initDrmmFilters());
    setBpcFilters(initBpcFilters());
    setSortField('');
    setSortDir('asc');
    setCurrentPage(1);

    // Fetch BPC tenant mappings (lazy — once per session). Failure is non-fatal:
    // we treat the selected tenant as unmapped and show all BPC devices.
    let bpcList = bpcTenantsList;
    if (bpcList === null) {
      try {
        const result = await fetchBpcTenants();
        bpcList = Array.isArray(result) ? result : [];
      } catch (err) {
        console.warn('[Coverage] fetchBpcTenants failed; BPC will load all tenants:', err);
        bpcList = [];
      }
      setBpcTenantsList(bpcList);
    }

    // BPC scoping: if a specific M365 tenant is selected and we have a mapping → scope BPC by that mapping.
    // If a specific tenant is selected without a mapping → show empty BPC + flag for banner.
    // If no tenant selected (all tenants) → load all BPC tenants (existing behavior).
    let bpcMode; // 'scoped' | 'unmapped' | 'all'
    let mappedBpcId = null;
    if (selectedTenant) {
      mappedBpcId = (bpcList.find((b) => b.m365TenantId === selectedTenant) || {}).bpcTenantId || null;
      bpcMode = mappedBpcId ? 'scoped' : 'unmapped';
    } else {
      bpcMode = 'all';
    }

    const t0 = performance.now();
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const isToday = snapshotDate === today;
    const baseOpts = snapshotDate ? { snapshotDate } : {};

    const loadWorkload = (workload, setDevices, setLoadTime, setError, setLoading, tenantOverride, extraOpts = {}) => {
      const opts = { ...baseOpts, ...extraOpts, workload };
      const tenant = tenantOverride !== undefined ? tenantOverride : (selectedTenant || '');
      fetchDeviceInventory(tenant, opts)
        .then((data) => {
          const arr = Array.isArray(data) ? data : [];
          if (arr.length === 0 && isToday) {
            return fetchDeviceInventory(tenant, { ...opts, snapshotDate: yesterday })
              .then((d2) => {
                const arr2 = Array.isArray(d2) ? d2 : [];
                if (arr2.length > 0) setSnapshotDate(yesterday);
                return arr2;
              });
          }
          return arr;
        })
        .then((arr) => {
          const secs = ((performance.now() - t0) / 1000).toFixed(1);
          setLoadTime(secs);
          setDevices(arr);
        })
        .catch((err) => setError(err.message || `Failed to load ${workload.toUpperCase()} devices.`))
        .finally(() => setLoading(false));
    };

    loadWorkload('m365',  setM365Devices,  setM365LoadTime,  setM365Error,  setM365Loading);
    loadWorkload('drmm',  setDrmmDevices,  setDrmmLoadTime,  setDrmmError,  setDrmmLoading);

    if (bpcMode === 'scoped') {
      loadWorkload('bpc', setBpcDevices, setBpcLoadTime, setBpcError, setBpcLoading,
                   '', { bpcTenantId: mappedBpcId });
    } else if (bpcMode === 'unmapped') {
      // Selected tenant has no BPC mapping. Don't fetch — show 0 devices + banner.
      setBpcDevices([]);
      setBpcLoading(false);
      setBpcLoadTime('0.0');
      setBpcUnmapped(true);
    } else {
      // All tenants → load every BPC tenant's devices (no PartitionKey filter).
      loadWorkload('bpc', setBpcDevices, setBpcLoadTime, setBpcError, setBpcLoading, '');
    }
  }, [selectedTenant, snapshotDate, bpcTenantsList]);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) { setSortDir((d) => d === 'asc' ? 'desc' : 'asc'); return field; }
      setSortDir('asc');
      return field;
    });
    setCurrentPage(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    if (workload === 'drmm') setDrmmFilters((p) => ({ ...p, [key]: value }));
    else if (workload === 'bpc') setBpcFilters((p) => ({ ...p, [key]: value }));
    else setM365Filters((p) => ({ ...p, [key]: value }));
    setCurrentPage(1);
  }, [workload]);

  const clearFilters = useCallback(() => {
    if (workload === 'drmm') setDrmmFilters(initDrmmFilters());
    else if (workload === 'bpc') setBpcFilters(initBpcFilters());
    else setM365Filters(initM365Filters());
    setCurrentPage(1);
  }, [workload]);

  // Distinct values for select-type column filters
  const m365Distinct = useMemo(() => ({
    osType:    [...new Set((m365Devices || []).map((d) => d.osType).filter(Boolean))].sort(),
    trustType: [...new Set((m365Devices || []).map((d) => d.trustType).filter(Boolean))].sort(),
  }), [m365Devices]);

  const drmmDistinct = useMemo(() => ({
    osCommonName: [...new Set((drmmDevices || []).map((d) => d.osCommonName).filter(Boolean))].sort(),
    drmmInstance: [...new Set((drmmDevices || []).map((d) => d.drmmInstance).filter(Boolean))].sort(),
  }), [drmmDevices]);

  const bpcDistinct = useMemo(() => ({
    bpcTenant: [...new Set((bpcDevices || []).map((d) => d.bpcTenant).filter(Boolean))].sort(),
    type:      [...new Set((bpcDevices || []).map((d) => d.type).filter(Boolean))].sort(),
    osName:    [...new Set((bpcDevices || []).map((d) => d.osName).filter(Boolean))].sort(),
  }), [bpcDevices]);

  // Active workload's data, filters, distinct values
  const activeDevices  = workload === 'drmm' ? drmmDevices  : workload === 'bpc' ? bpcDevices  : m365Devices;
  const activeLoading  = workload === 'drmm' ? drmmLoading  : workload === 'bpc' ? bpcLoading  : m365Loading;
  const activeError    = workload === 'drmm' ? drmmError    : workload === 'bpc' ? bpcError    : m365Error;
  const activeFilters  = workload === 'drmm' ? drmmFilters  : workload === 'bpc' ? bpcFilters  : m365Filters;
  const activeCols     = workload === 'drmm' ? DRMM_COLS    : workload === 'bpc' ? BPC_COLS    : M365_COLS;
  const activeDistinct = workload === 'drmm' ? drmmDistinct : workload === 'bpc' ? bpcDistinct : m365Distinct;

  // Filter → sort → paginate
  const filtered = useMemo(() => {
    if (!activeDevices) return [];
    let rows = applyColFilters(activeDevices, activeCols, activeFilters);
    if (sortField) rows = [...rows].sort((a, b) => compareVals(a[sortField], b[sortField], sortDir));
    return rows;
  }, [activeDevices, activeCols, activeFilters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const activeFilterCount = countActiveFilters(activeFilters);

  const anyLoaded = m365Devices !== null || drmmDevices !== null || bpcDevices !== null
                 || m365Loading || drmmLoading || bpcLoading;

  // Workload button label helpers
  function workloadLabel(wl) {
    if (wl === 'm365') {
      if (m365Loading) return 'Seen By M365 (loading…)';
      if (m365Devices !== null) return `Seen By M365 (${m365Devices.length.toLocaleString()})`;
      return 'Seen By M365';
    }
    if (wl === 'drmm') {
      if (drmmLoading) return 'DRMM (loading…)';
      if (drmmDevices !== null) return `DRMM (${drmmDevices.length.toLocaleString()})`;
      return 'DRMM';
    }
    if (wl === 'bpc') {
      if (bpcLoading) return 'BPC (loading…)';
      if (bpcDevices !== null) return `BPC (${bpcDevices.length.toLocaleString()})`;
      return 'BPC';
    }
    return wl;
  }

  return (
    <div className="p-6 space-y-6">
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div>
        <div className="text-xs text-gray-500 mb-1">Coverage Analysis</div>
        <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <ComputerDesktopIcon className="h-5 w-5 text-blue-400" />
          Device Inventory
        </h1>
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────────────── */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Tenant picker */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Tenant</label>
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              disabled={tenantsLoading}
              className="input-field py-1 px-2 text-xs min-w-[180px]"
            >
              <option value="">All tenants (very slow)</option>
              {tenants
                .map((t) => {
                  const shortName = t.ShortName;
                  const friendlyName = t.FriendlyName;
                  const label = shortName && friendlyName
                    ? `(${shortName}) ${friendlyName}`
                    : (shortName || friendlyName || t.Domain || t.TenantID);
                  return { id: t.TenantID, label };
                })
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          {/* Snapshot Date — applies to all workloads */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Snapshot Date</label>
            <input
              type="date"
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
              className="input-field py-1 px-2 text-xs w-36"
            />
            {snapshotDate && (
              <button type="button" onClick={() => setSnapshotDate('')} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                Clear
              </button>
            )}
          </div>

          {/* Workload filter (acts as view selector once loaded) */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-400">Workload</span>
            {['m365', 'drmm', 'bpc'].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setWorkload(value)}
                className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                  workload === value
                    ? 'bg-blue-600/20 text-blue-400 border-blue-500/50'
                    : 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {workloadLabel(value)}
              </button>
            ))}
          </div>

          {/* Load button */}
          <button
            type="button"
            onClick={handleLoad}
            disabled={m365Loading || drmmLoading || bpcLoading}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {(m365Loading || drmmLoading || bpcLoading) ? 'Loading…' : 'Load Devices'}
          </button>
        </div>

        {/* Load time display — shown after first load */}
        {(m365LoadTime || drmmLoadTime || bpcLoadTime) && (
          <div className="flex gap-4 mt-2 pt-2 border-t border-gray-800/60">
            {m365LoadTime && (
              <span className="text-[11px] text-gray-600">
                Seen By M365: <span className="text-gray-500">{m365LoadTime}s</span>
                {m365Devices && <span className="text-gray-600"> · {m365Devices.length.toLocaleString()} devices</span>}
              </span>
            )}
            {drmmLoadTime && (
              <span className="text-[11px] text-gray-600">
                DRMM: <span className="text-gray-500">{drmmLoadTime}s</span>
                {drmmDevices && <span className="text-gray-600"> · {drmmDevices.length.toLocaleString()} devices</span>}
              </span>
            )}
            {bpcLoadTime && (
              <span className="text-[11px] text-gray-600">
                BPC: <span className="text-gray-500">{bpcLoadTime}s</span>
                {bpcDevices && <span className="text-gray-600"> · {bpcDevices.length.toLocaleString()} devices</span>}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Per-workload error banners ────────────────────────────────────────── */}
      {activeError && (
        <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
          <span className="flex-1">{activeError}</span>
          <button onClick={handleLoad} className="text-xs underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* ── Results ──────────────────────────────────────────────────────────── */}
      {anyLoaded && (
        <div className="card overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-800 min-h-[40px]">
            {activeDevices !== null && !activeLoading && (
              <span className="text-xs text-gray-500">
                {filtered.length} {filtered.length === 1 ? 'device' : 'devices'}
                {activeFilterCount > 0 && activeDevices.length !== filtered.length
                  ? ` (filtered from ${activeDevices.length})`
                  : ''}
              </span>
            )}
            {activeLoading && (
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="animate-spin inline-block h-3 w-3 border-b border-blue-500 rounded-full" />
                Loading {workload.toUpperCase()} devices…
              </span>
            )}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
                Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* BPC mapping warning */}
          {workload === 'bpc' && bpcUnmapped && (
            <div className="mx-4 my-2 flex items-start gap-3 p-3 bg-amber-900/20 border border-amber-700/40 rounded text-xs text-amber-200">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-amber-400 mt-0.5" />
              <div>
                <p className="font-medium">No BlackPoint Cyber tenant is mapped to the selected M365 tenant.</p>
                <p className="text-[11px] text-amber-300/80 mt-0.5">
                  Map this tenant in Admin → Tenants → BlackPoint Cyber MDR Tenants to scope BPC devices, or pick "All tenants" to see every BPC device.
                </p>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {workload === 'drmm' ? (
              <DRMMTable
                rows={paged} loading={activeLoading}
                sortField={sortField} sortDir={sortDir} onSort={handleSort}
                filters={drmmFilters} onFilterChange={setFilter} distinct={drmmDistinct}
              />
            ) : workload === 'bpc' ? (
              <BPCTable
                rows={paged} loading={activeLoading}
                sortField={sortField} sortDir={sortDir} onSort={handleSort}
                filters={bpcFilters} onFilterChange={setFilter} distinct={bpcDistinct}
              />
            ) : (
              <M365Table
                rows={paged} loading={activeLoading}
                sortField={sortField} sortDir={sortDir} onSort={handleSort}
                filters={m365Filters} onFilterChange={setFilter} distinct={m365Distinct}
              />
            )}
          </div>

          {/* Pagination */}
          {!activeLoading && filtered.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filtered.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── M365 table ────────────────────────────────────────────────────────────────

function M365Table({ rows, loading, sortField, sortDir, onSort, filters, onFilterChange, distinct }) {
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(M365_COLS.map((c) => c.w));
  return (
    <table ref={tableRef} className="text-xs text-gray-300" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
      <TableHead cols={M365_COLS} colWidths={colWidths} thRefs={thRefs} startResize={startResize}
        sortField={sortField} sortDir={sortDir} onSort={onSort}
        filters={filters} onFilterChange={onFilterChange} distinct={distinct} />
      <tbody>
        {loading ? (
          <tr><td colSpan={M365_COLS.length} className="px-3 py-8 text-center text-gray-500">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />Loading devices…
            </div>
          </td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={M365_COLS.length} className="px-3 py-8 text-center text-gray-500">No devices found</td></tr>
        ) : rows.map((d) => (
          <tr key={`${d.tenantId}-${d.deviceId}`} className="border-b border-gray-800/50 hover:bg-gray-800/30">
            <td className="px-3 py-2 font-mono truncate" title={d.deviceName}>{d.deviceName || '—'}</td>
            <td className="px-3 py-2"><OSBadge value={d.osType} /></td>
            <td className="px-3 py-2 text-gray-400">{d.trustType || '—'}</td>
            <td className="px-3 py-2 text-center"><BoolCell value={d.isCompliant} /></td>
            <td className="px-3 py-2 text-center"><BoolCell value={d.isManaged} /></td>
            <td className="px-3 py-2 text-gray-400">{formatDate(d.lastSeenUtc)}</td>
            <td className="px-3 py-2 text-gray-400 truncate" title={d.lastUserUpn}>{d.lastUserUpn || '—'}</td>
            <td className="px-3 py-2 text-gray-500 text-xs">{d.snapshotDate || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── DRMM table ────────────────────────────────────────────────────────────────

function DRMMTable({ rows, loading, sortField, sortDir, onSort, filters, onFilterChange, distinct }) {
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(DRMM_COLS.map((c) => c.w));
  return (
    <table ref={tableRef} className="text-xs text-gray-300" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
      <TableHead cols={DRMM_COLS} colWidths={colWidths} thRefs={thRefs} startResize={startResize}
        sortField={sortField} sortDir={sortDir} onSort={onSort}
        filters={filters} onFilterChange={onFilterChange} distinct={distinct} />
      <tbody>
        {loading ? (
          <tr><td colSpan={DRMM_COLS.length} className="px-3 py-8 text-center text-gray-500">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />Loading devices…
            </div>
          </td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={DRMM_COLS.length} className="px-3 py-8 text-center text-gray-500">No devices found</td></tr>
        ) : rows.map((d) => (
          <tr key={`${d.tenantId}-${d.drmmInstance}-${d.deviceuid}`} className="border-b border-gray-800/50 hover:bg-gray-800/30">
            <td className="px-3 py-2 font-mono truncate" title={d.hostname}>{d.hostname || '—'}</td>
            <td className="px-3 py-2"><OSBadge value={d.osCommonName} /></td>
            <td className="px-3 py-2">
              {d.drmmInstance
                ? <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border bg-gray-800/60 text-gray-300 border-gray-700/50">{d.drmmInstance}</span>
                : '—'}
            </td>
            <td className="px-3 py-2 text-gray-400 truncate" title={d.site}>{d.site || '—'}</td>
            <td className="px-3 py-2 text-gray-400 truncate" title={d.lastLoggedInUser}>{d.lastLoggedInUser || '—'}</td>
            <td className="px-3 py-2 text-center">
              <span className={`inline-block h-2 w-2 rounded-full ${d.online ? 'bg-green-400' : 'bg-gray-600'}`} title={d.online ? 'Online' : 'Offline'} />
            </td>
            <td className="px-3 py-2 text-gray-400">{formatDate(d.lastSeen)}</td>
            <td className="px-3 py-2 text-gray-400">{d.collectionDate || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── BPC posture cell helpers ───────────────────────────────────────────────────

function EolCell({ value }) {
  if (value) return <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border bg-red-900/40 text-red-300 border-red-700/50">EOL</span>;
  return <span className="text-gray-600 text-xs">—</span>;
}

function PostureBoolCell({ value }) {
  if (value === null || value === undefined) return <span className="text-gray-600 text-xs">—</span>;
  return value
    ? <span className="inline-flex items-center justify-center"><span className="h-2 w-2 rounded-full bg-green-400" /></span>
    : <span className="inline-flex items-center justify-center"><span className="h-2 w-2 rounded-full bg-red-500" /></span>;
}

// ── BPC table ─────────────────────────────────────────────────────────────────

function BPCTable({ rows, loading, sortField, sortDir, onSort, filters, onFilterChange, distinct }) {
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(BPC_COLS.map((c) => c.w));
  return (
    <table ref={tableRef} className="text-xs text-gray-300" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
      <TableHead cols={BPC_COLS} colWidths={colWidths} thRefs={thRefs} startResize={startResize}
        sortField={sortField} sortDir={sortDir} onSort={onSort}
        filters={filters} onFilterChange={onFilterChange} distinct={distinct} />
      <tbody>
        {loading ? (
          <tr><td colSpan={BPC_COLS.length} className="px-3 py-8 text-center text-gray-500">
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />Loading devices…
            </div>
          </td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={BPC_COLS.length} className="px-3 py-8 text-center text-gray-500">No devices found</td></tr>
        ) : rows.map((d) => (
          <tr key={`${d.bpcTenantId}-${d.deviceId}`} className="border-b border-gray-800/50 hover:bg-gray-800/30">
            <td className="px-3 py-2 font-mono truncate" title={d.hostname}>{d.hostname || '—'}</td>
            <td className="px-3 py-2 text-gray-400 truncate">{d.bpcTenant || '—'}</td>
            <td className="px-3 py-2 text-gray-400">{d.type || '—'}</td>
            <td className="px-3 py-2"><OSBadge value={d.osName === 'OSX' ? 'MacOS' : d.osName} /></td>
            <td className="px-3 py-2 text-gray-400 truncate" title={d.osVersion}>{d.osVersion || '—'}</td>
            <td className="px-3 py-2 text-center"><EolCell value={d.osIsEol} /></td>
            <td className="px-3 py-2 text-center"><PostureBoolCell value={d.firewallEnabled} /></td>
            <td className="px-3 py-2 text-center"><PostureBoolCell value={d.windowsDefenderEnabled} /></td>
            <td className="px-3 py-2 text-center"><PostureBoolCell value={d.encrypted} /></td>
            <td className="px-3 py-2 text-gray-400">{d.agentVersion || '—'}</td>
            <td className="px-3 py-2 text-gray-400">{formatDate(d.agentLastSeenOn)}</td>
            <td className="px-3 py-2 text-gray-500 text-xs">{d.collectionDate || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
